import { iUserData, iUser } from '@/entities/user/user.types';
import User from '@/entities/user/user';
import { iAuthService } from './auth.types';
import { iUserService } from '../user/user.types';
import { iFirestoreAuthService, FirestoreAuthUser } from '../firestore/firestore.types';

export default class AuthService implements iAuthService {
  private userService: iUserService;

  private firebaseAuth: iFirestoreAuthService;

  constructor(
    firebaseAuth: iFirestoreAuthService,
    userService: iUserService,
  ) {
    this.userService = userService;
    this.firebaseAuth = firebaseAuth;
  }

  async register(user: iUserData): Promise<void> {
    const fireUser = await this.firebaseAuth
      .createUser(user.email, user.password);

    if (!fireUser) return;

    await fireUser
      .updateProfile({
        displayName: user.username,
      });

    await this.createUser(fireUser, user);

    fireUser.sendEmailVerification();
  }

  async login(username: string, password: string): Promise<iUser> {
    try {
      const user = await this.userService.findByUsername(username);

      const auth = await this.firebaseAuth
        .signIn(user.email, password);

      /* istanbul ignore next */
      user.emailVerified = auth?.emailVerified || false;

      if (!user.emailVerified) {
        throw new Error('user_email_verification');
      }

      return user;
    } catch (error) {
      console.error(error);

      if (error && error.code === 'auth/user-not-found') {
        throw new Error('user_does_not_exists');
      }

      throw error;
    }
  }

  async onAuthChanged(firebaseUser: FirestoreAuthUser) {
    if (!firebaseUser.displayName) {
      return null;
    }

    const user = await this.userService.findByUsername(firebaseUser.displayName);

    if (user) {
      user.emailVerified = firebaseUser.emailVerified || false;
      return user;
    }

    return null;
  }

  isAuthenticated(): Promise<boolean> {
    return this.firebaseAuth.isAuthenticated();
  }

  async logout() {
    try {
      await this.firebaseAuth.signOut();
    } catch (error) {
      throw new Error('logout_error');
    }
  }

  private async createUser(fireUser: FirestoreAuthUser, userData: iUserData) {
    const user = new User(
      fireUser.uid,
      userData.username,
      userData.email,
      userData.gamertag,
    );

    await this.userService.create(user);
  }
}
